import { useLocalStore, useAsObservableSource } from 'mobx-react-lite';

import { IcCalendarIcon, IcCallIcon, IcConferenceIcon, IcVideoOnIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopover, usePopover, IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';

interface IStartCallButtonParams {
  groupId: string;
  startAudioCall: (() => unknown) | null;
  startVideoCall: (() => unknown) | null;
  startConferenceCall: (() => unknown) | null;
  scheduleCallEvent(): void;
}

interface IStartCallButtonPresenter {
  menuItems: IPopoverMenuItem[];
  onClick(): Promise<void>;
}

interface IUseStartCallButtonPresenter {
  presenter: IStartCallButtonPresenter;
  popover: IPopover<HTMLButtonElement>;
}

export const useStartCallButtonPresenter = (
  data: IStartCallButtonParams
): IUseStartCallButtonPresenter => {
  const { groupId, startAudioCall, startVideoCall, startConferenceCall, scheduleCallEvent } = data;
  const { t } = useTranslation();
  const popover = usePopover<HTMLButtonElement>();
  const source = useAsObservableSource({
    groupId,
    startAudioCall,
    startVideoCall,
    startConferenceCall,
    scheduleCallEvent,
  });

  const presenter = useLocalStore<IStartCallButtonPresenter>(() => ({
    get menuItems(): IPopoverMenuItem[] {
      const items = [];

      if (source.startAudioCall) {
        items.push({
          key: 'audioCall',
          title: t('group_chat_audio_call_button'),
          onClick: source.startAudioCall,
          icon: IcCallIcon,
          isVisible: true,
        });
      }

      if (source.startVideoCall) {
        items.push({
          key: 'videoCall',
          title: t('group_chat_video_call_button'),
          onClick: source.startVideoCall,
          icon: IcVideoOnIcon,
          isVisible: true,
        });
      }
      if (source.startConferenceCall) {
        items.push({
          key: 'conferenceCall',
          title: t('calls_conference'),
          onClick: source.startConferenceCall,
          icon: IcConferenceIcon,
          isVisible: true,
        });
      }

      if (Features.isScheduledCallEnabled()) {
        items.push({
          key: 'scheduledCall',
          title: t('call_scheduled_call'),
          onClick: source.scheduleCallEvent,
          icon: IcCalendarIcon,
        });
      }

      return items;
    },

    async onClick(): Promise<void> {
      const group = Entities.GroupsStore.getGroupById(source.groupId);
      if (group && group.isOpen && group.withoutMe) {
        const isNeedJoin = await Tasks.app.showConfirm({
          text: t('public_group_call_alert'),
          primaryText: t('calls_join'),
          secondaryText: t('cancel'),
        });

        if (isNeedJoin) {
          await Tasks.group.joinGroup({ groupId: source.groupId });
        }

        return;
      }

      if (source.startAudioCall) {
        source.startAudioCall();

        return;
      }
      popover.toggle();
    },
  }));

  return { presenter, popover };
};
